"use client";

import { useState } from "react";
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { SliderWrapper } from "@/components/common/slider-wrapper";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { mapViewModelToItem } from "@/components/common/history-and-favourite/view-model-to-transformer";
import { cn } from "@/lib/utils";
import { historyAndFavouriteSections } from "@/components/common/history-and-favourite/sections";
import { SuperLink as Link } from "@/components/common/super-link";
import { ChevronRightIcon } from "lucide-react";
import { HistoryAndFavouriteListItem } from "@/components/common/history-and-favourite/components/history-and-favourite-list-item";

export const HistoryAndFavouriteList = ({
  items,
  link,
  createItemPath,
  section,
  isFavourite = false,
}: {
  items: HistoryAndFavouriteRawItem[];
  link: string | null;
  createItemPath: null | ((item: any) => string);
  section?: HistoryAndFavouriteSection;
  isFavourite?: boolean;
}) => {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const over5Items = items.length > 5;

  const mapViewModelToSection: Record<string, HistoryAndFavouriteSection> = {
    tutorialPreview: "tutorial",
    fastQuestionPreview: "fast_question",
    calculatorPreview: "calculator",
    medGroupPreview: "meds",
    icd10Preview: "icd10",
    articlePreview: "article",
    medicalSupplyPreview: "medical_supply",
    clinicalCasePreview: "clinical_case",
    coursePreview: "course",
    ebookPreview: "ebook",
    ResidencySpecializationExtendedPreview: "residency_specialization",
    playlistTreatedAsVideoPreview: "video",
    playlistPreview: "playlist",
    playlistEpisodePreview: "playlist_episode",
  };

  return items.length > 0 ? (
    <div
      className={"flex flex-col gap-6 sm:flex-row sm:justify-between sm:gap-12"}
    >
      <SliderWrapper api={api}>
        <Carousel
          opts={{
            skipSnaps: true,
          }}
          setApi={setApi}
          plugins={[WheelGesturesPlugin()]}
        >
          <CarouselContent>
            {items.map((rawItem, index) => {
              const itemSection =
                section ?? mapViewModelToSection[rawItem.viewModelType];
              const item = mapViewModelToItem(itemSection, rawItem);

              if (!item) {
                return null;
              }

              return (
                <CarouselItem
                  key={item.title}
                  isFirst={index === 0}
                  className={cn("ml-3 grid basis-auto")}
                  isLast={over5Items && index === 5}
                >
                  <HistoryAndFavouriteListItem
                    key={item.title}
                    item={item}
                    createItemPath={
                      createItemPath ||
                      historyAndFavouriteSections[itemSection].createItemPath
                    }
                    isFavourite={isFavourite}
                  />
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      </SliderWrapper>
      {link && items.length > 5 && (
        <Link
          className={`hover:text-remedium-blue flex w-[130px] min-w-[130px] items-center justify-between gap-2 text-xs font-normal text-zinc-900`}
          href={link}
        >
          Zobacz wszystkie
          <ChevronRightIcon size={16} />
        </Link>
      )}
    </div>
  ) : (
    <div className={"text-muted-foreground text-center text-xs leading-[41px]"}>
      Lista jest pusta
    </div>
  );
};
